import React, { useEffect, useRef, useState } from 'react';
import {
  ActionBarPrimitive,
  AttachmentPrimitive,
  AuiIf,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePartPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAuiEvent,
  unstable_useTriggerPopoverScopeContext,
  useAui,
  useAuiState,
  useThreadViewport,
} from '@assistant-ui/react';

import { AttachedFile, AttachedImage } from './Attachments';
import { humanSize } from '../../services/files';
import ChatHeader from './ChatHeader';
import { useChats } from './ChatRuntimeProvider';
import Markdown, { ReasoningMarkdown } from './Markdown';
import { useRunProgress } from './ChatRuntimeProvider';
import { useSelection } from './SelectionContext';
import PromptArguments from './PromptArguments';
import { useMcpPrompts } from './useMcpPrompts';
import {
  AlertIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  BrainIcon,
  BranchIcon,
  CheckIcon,
  ChevronIcon,
  CloseIcon,
  CopyIcon,
  FileIcon,
  ImageIcon,
  PaperclipIcon,
  PencilIcon,
  RefreshIcon,
  StopIcon,
  WrenchIcon,
} from './icons';

export default function Thread() {
  return (
    <ThreadPrimitive.Root className="flex-1 flex flex-col min-w-0 min-h-0 bg-gray-50 text-gray-900 relative">
      <ChatHeader />

      <ThreadPrimitive.Viewport className="flex-1 min-h-0 overflow-y-auto scroll-smooth px-4">
        <ScrollOnOpen />

        <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 py-8">
          <AuiIf condition={(s) => s.thread.isEmpty}>
            <Welcome />
          </AuiIf>

          <ThreadPrimitive.Messages>
            {({ message }) =>
              message.role === 'user' ? <UserMessage /> : <AssistantMessage />
            }
          </ThreadPrimitive.Messages>
        </div>
      </ThreadPrimitive.Viewport>

      <div className="relative border-t border-gray-200 bg-white p-3 sm:p-4">
        <ScrollToBottom />
        <RunStatus />
        <Composer />
      </div>
    </ThreadPrimitive.Root>
  );
}

// The empty state says what to do next, which depends on how much has been
// chosen. A generic "How can I help?" is wrong on two counts: nothing can help
// until a bot is picked, and once one is, it should say which.
// Opening a chat imports its messages AFTER the thread switch, so the
// viewport's own scroll-on-switch has nothing to scroll yet and you land at the
// top of the history instead of at the latest message.
function ScrollOnOpen() {
  const { opened } = useChats();
  const scrollToBottom = useThreadViewport((v) => v.scrollToBottom);
  const count = useAuiState((s) => s.thread.messages.length);

  useEffect(() => {
    if (opened === 0 || count === 0) return;

    // after the imported messages have been laid out, or the scroll lands
    // against the height the viewport had a moment ago
    const frame = requestAnimationFrame(() => scrollToBottom({ behavior: 'instant' }));

    return () => cancelAnimationFrame(frame);
    // deliberately not on `count`: this is about opening a chat, not about
    // every message that arrives afterwards
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, scrollToBottom]);

  return null;
}

function Welcome() {
  const { project, projectTitle, bot, botTitle, canSend } = useSelection();

  let heading = 'Pick a project to start';
  let detail = 'Choose one in the sidebar, then pick a bot.';

  if (project && !bot) {
    heading = 'Pick a bot to start';
    detail = `Choose a bot in ${projectTitle || project} to talk to.`;
  }

  if (canSend) {
    heading = `Ask ${botTitle || bot}`;
    detail = `in ${projectTitle || project}`;
  }

  return (
    // centred in the viewport rather than pinned to the top: with no messages
    // there is nothing else on screen, and text sitting at the top of an empty
    // column reads as a page that failed to load
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
      {/* washed out until a bot is chosen, so "nothing selected" reads as
          waiting rather than as the real thing */}
      <img
        src={`${process.env.PUBLIC_URL || ''}/operator_plus_logo_hd.png`}
        alt=""
        className={`h-16 w-16 object-contain transition-opacity ${
          canSend ? 'opacity-100' : 'opacity-40 grayscale'
        }`}
      />

      <div>
        <h1 className="text-2xl font-semibold text-gray-900">{heading}</h1>
        <p className="mt-1.5 text-sm text-gray-500">{detail}</p>
      </div>
    </div>
  );
}

// What the backend is doing, for the whole run. Lives here rather than in the
// message because the message's Empty slot stops rendering the moment the first
// token arrives, which is exactly when tool and retrieval progress starts.
function RunStatus() {
  const running = useAuiState((s) => s.thread.isRunning);
  const progress = useRunProgress();

  if (!running) return null;

  return (
    <div className="mx-auto mb-2 flex w-full max-w-4xl items-center gap-2 px-1" role="status" aria-live="polite">
      <span className="flex items-center gap-1">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="h-1 w-1 rounded-full bg-gray-400 animate-bounce"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </span>

      <span className="truncate text-xs text-gray-500">{progress || 'Working...'}</span>
    </div>
  );
}

// promptNameOf pulls the prompt's own name back out of a command id.
//
// The id is "<imcp>:<name>", built in useMcpPrompts to keep two servers'
// identically named prompts apart. Read back here rather than carried
// alongside because the slash adapter drops everything but an icon on its way
// to a trigger item: toItem keeps id, label, description and metadata.icon,
// and nothing else. Change the id format there and this follows.
function promptNameOf(id) {
  const at = id.indexOf(':');

  return at === -1 ? id : id.slice(at + 1);
}

// One row of the "/" menu, which scrolls itself into view when the keyboard
// reaches it.
//
// The scrolling is ours because the scroll container is: TriggerPopover renders
// a plain div and the max-height and overflow on it are ours, so the library has
// nothing to scroll and does not try. What it does give us is the highlight, off
// the same scope context the popover uses, so this is reading its state rather
// than working out which row is current a second time.
//
// block: "nearest" so a row already on screen does not move, and neither does
// the page behind the menu.
function SlashCommandItem({ item, index }) {
  const { highlightedIndex } = unstable_useTriggerPopoverScopeContext();
  const row = useRef(null);
  const highlighted = index === highlightedIndex;

  useEffect(() => {
    if (highlighted) {
      row.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [highlighted]);

  return (
    <ComposerPrimitive.Unstable_TriggerPopoverItem
      ref={row}
      item={item}
      index={index}
      // data-[highlighted], not data-[highlighted=true]: the library sets the
      // attribute to an EMPTY string, so a value selector never matches and
      // the keyboard highlight was invisible.
      className="flex w-full flex-col items-start gap-0.5 rounded-lg border-l-2 border-transparent px-3 py-2 text-left hover:bg-gray-100 data-[highlighted]:border-primary-500 data-[highlighted]:bg-primary-50"
    >
      <span className="flex w-full items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          {/* alt is empty on purpose: the label beside it already says what
              this is, so a screen reader announcing the icon too would just
              repeat itself. */}
          {item.metadata?.icon && (
            <img src={item.metadata.icon} alt="" className="h-4 w-4 shrink-0 rounded-sm" />
          )}
          <span className="truncate text-sm text-gray-900">{item.label}</span>
        </span>

        {/* The name, because the title is what you read and the name is what
            you type. Both already match, so "/draw-gauge" narrows to one row
            the same as "gauge" does; without showing it, "/draw" listed six
            rows and gave no hint what came next. */}
        <span className="shrink-0 font-mono text-xs text-gray-400">/{promptNameOf(item.id)}</span>
      </span>

      {item.description && <span className="text-xs text-gray-500">{item.description}</span>}
    </ComposerPrimitive.Unstable_TriggerPopoverItem>
  );
}

function Composer() {
  const { project, bot, botTitle, canSend, canSendImages, limits } = useSelection();

  const aui = useAui();
  // ComposerPrimitive.Input composes our ref with its own textarea ref, so this
  // is the supported way to reach it. The library focuses itself on run start
  // and thread switch, but exposes no imperative focus for a third case.
  const composerInput = useRef(null);

  // "/" offers the prompts every MCP behind this bot's agents publishes. The
  // popover, the filtering and the keyboard are assistant-ui's; the hook
  // fetches the list and renders the one that gets picked.
  //
  // The rendered prompt lands as ordinary composer text for somebody to read
  // and edit before sending, so the send path and the stored conversation know
  // nothing about prompts. Appended rather than replacing, because whatever
  // was already typed is theirs: removeOnExecute has already taken the
  // "/draw-bar" out by the time this runs.
  const slash = useMcpPrompts({
    project,
    bot,
    onText: (rendered) => {
      const current = aui.composer.getState().text;
      aui.composer.setText(current ? `${current.trimEnd()}\n\n${rendered.text}` : rendered.text);

      // Back to the composer with the caret at the end, so Enter sends and
      // typing appends. Deliberately NOT the send button: Enter there would
      // send too, but the whole reason a prompt lands in the composer instead
      // of going straight out is that somebody can read it and add to it
      // first, and a focused button cannot be typed into.
      //
      // After a frame, because setText goes through the store and the textarea
      // has not got the new value yet: setting the caret now would put it at
      // the end of the OLD text.
      requestAnimationFrame(() => {
        const textarea = composerInput.current;
        if (!textarea) return;

        textarea.focus({ preventScroll: true });
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      });
    },
  });

  // A file the composer refused. Shown here rather than as a toast because the
  // draft is still on screen and the fix (pick a smaller one) belongs next to
  // the button that was just used.
  const [rejected, setRejected] = useState('');

  useAuiEvent('composer.attachmentAddError', ({ message }) => setRejected(message));
  useAuiEvent('composer.attachmentAdd', () => setRejected(''));
  useAuiEvent('composer.send', () => setRejected(''));

  const attached = useAuiState(({ composer }) => composer.attachments.length);
  const full = attached >= limits.maxFilesPerMessage;

  const placeholder = !project
    ? 'Select a project first...'
    : !bot
      ? 'Select a bot first...'
      : `Send a message to ${botTitle}... Enter to send, Shift+Enter for a new line.`;

  return (
    // asChild so the dropzone's handlers land on the composer itself: dropping
    // a file anywhere on it attaches, and an unhandled drop would otherwise
    // navigate the tab away from the chat.
    <ComposerPrimitive.AttachmentDropzone asChild disabled={!canSend}>
      <ComposerPrimitive.Root className="mx-auto flex w-full max-w-4xl flex-col gap-1 rounded-2xl border border-gray-200 bg-white p-2 shadow-card focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 data-[dragging=true]:border-primary-500 data-[dragging=true]:bg-primary-50">
      {/* The root must be an ANCESTOR of ComposerPrimitive.Input, not a
          sibling: it provides the input plugin registry, and the Input reads
          that registry to route keydown into the popover and to report the
          caret back. Outside it the popover still opens off the composer text
          and clicking an item still works, but the arrow keys do nothing and
          Enter sends "/draw-bar" as a message instead of running it.
          Providers only, no DOM, so the flex layout is unchanged. */}
      <ComposerPrimitive.Unstable_TriggerPopoverRoot>
      {slash.pending && (
        <PromptArguments
          project={project}
          bot={bot}
          pending={slash.pending}
          busy={slash.busy}
          error={slash.error}
          onSubmit={slash.submit}
          onCancel={slash.cancel}
        />
      )}

      {/* What the render did to the prompt, when it did anything worth saying:
          a conversation flattened for editing, or binary parts left out. Above
          the composer and dismissible, because it describes the text sitting
          in it right now. */}
      {slash.note && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertIcon className="mt-px h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{slash.note}</span>
          <button
            type="button"
            onClick={slash.dismissNote}
            aria-label="Dismiss"
            className="shrink-0 rounded p-0.5 hover:bg-amber-100"
          >
            <CloseIcon className="h-3.5 w-3.5" />
          </button>
        </p>
      )}

      {/* A render that failed with no form open still has to say so, or the
          slash key just swallowed the command. */}
      {!slash.pending && slash.error && (
        <p className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertIcon className="mt-px h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{slash.error}</span>
          <button
            type="button"
            onClick={slash.cancel}
            aria-label="Dismiss"
            className="shrink-0 rounded p-0.5 hover:bg-red-100"
          >
            <CloseIcon className="h-3.5 w-3.5" />
          </button>
        </p>
      )}

      {slash.hasPrompts && (
          <ComposerPrimitive.Unstable_TriggerPopover
            char="/"
            adapter={slash.adapter}
            className="z-20 max-h-72 w-full max-w-xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-lg"
          >
            <ComposerPrimitive.Unstable_TriggerPopover.Action {...slash.action} />
            <ComposerPrimitive.Unstable_TriggerPopoverItems>
              {(items) => items.map((item, index) => (
                <SlashCommandItem key={item.id} item={item} index={index} />
              ))}
            </ComposerPrimitive.Unstable_TriggerPopoverItems>
          </ComposerPrimitive.Unstable_TriggerPopover>
      )}

      {rejected && (
        <p className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertIcon className="mt-px h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{rejected}</span>
          <button
            type="button"
            onClick={() => setRejected('')}
            aria-label="Dismiss"
            className="shrink-0 rounded p-0.5 hover:bg-red-100"
          >
            <CloseIcon className="h-3.5 w-3.5" />
          </button>
        </p>
      )}

      <AuiIf condition={(s) => s.composer.attachments.length > 0}>
        <div className="flex flex-wrap gap-2 px-1 pt-1">
          <ComposerPrimitive.Attachments>
            {({ attachment }) => <AttachmentChip attachment={attachment} />}
          </ComposerPrimitive.Attachments>
        </div>
      </AuiIf>

      <div className="flex w-full items-end gap-2">
        <ComposerPrimitive.AddAttachment
          disabled={!canSend || full}
          title={full
            ? `At most ${limits.maxFilesPerMessage} files per message`
            : canSendImages ? 'Attach a file or image' : 'Attach a file'}
          aria-label="Attach a file"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <PaperclipIcon className="h-4 w-4" />
        </ComposerPrimitive.AddAttachment>

        <ComposerPrimitive.Input
          ref={composerInput}
          autoFocus
          rows={1}
          disabled={!canSend}
          placeholder={placeholder}
          aria-label="Message input. Enter to send, Shift plus Enter for a new line."
          className="max-h-40 flex-1 resize-none bg-transparent px-2 py-2 text-base text-gray-900 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
        />

        {/* send and stop are the same slot: only one of them is ever live */}
        <AuiIf condition={(s) => !s.thread.isRunning}>
          <ComposerPrimitive.Send
            disabled={!canSend}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
            aria-label="Send"
          >
            <ArrowUpIcon className="h-4 w-4" />
          </ComposerPrimitive.Send>
        </AuiIf>

        <AuiIf condition={(s) => s.thread.isRunning}>
          <ComposerPrimitive.Cancel
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white transition-colors hover:bg-gray-700"
            aria-label="Stop"
          >
            <StopIcon className="h-3.5 w-3.5" />
          </ComposerPrimitive.Cancel>
        </AuiIf>
      </div>
      </ComposerPrimitive.Unstable_TriggerPopoverRoot>
      </ComposerPrimitive.Root>
    </ComposerPrimitive.AttachmentDropzone>
  );
}

// One file the user sent, in the transcript. Name and size only: a preview
// would claim the model looked at the picture, which is not what happens, and
// for a log or a csv there is nothing to preview anyway.
function SentAttachment({ attachment }) {
  const Icon = attachment.type === 'image' ? ImageIcon : FileIcon;
  const size = attachment.bytes ?? attachment.file?.size;

  return (
    <AttachmentPrimitive.Root className="flex max-w-[14rem] items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2">
      <Icon className="h-4 w-4 shrink-0 text-gray-400" />

      {/* Name renders bare text and drops className, so the truncation
          has to live on a span of our own */}
      <span className="min-w-0">
        <span className="block truncate text-sm text-gray-800" title={attachment.name}>
          <AttachmentPrimitive.Name />
        </span>
        {size > 0 && <span className="block text-xs text-gray-500">{humanSize(size)}</span>}
      </span>
    </AttachmentPrimitive.Root>
  );
}

// One attached file, before it's sent. Removing it deletes the upload, which is
// why this is a real button and not just a visual dismiss.
function AttachmentChip({ attachment }) {
  const uploading = attachment.status?.type === 'running';
  const Icon = attachment.type === 'image' ? ImageIcon : FileIcon;

  return (
    <AttachmentPrimitive.Root className="flex max-w-[14rem] items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs">
      <Icon className={`h-4 w-4 shrink-0 ${uploading ? 'animate-pulse text-gray-300' : 'text-gray-400'}`} />

      <span className="min-w-0 flex-1 truncate text-gray-700" title={attachment.name}>
        <AttachmentPrimitive.Name />
      </span>

      <AttachmentPrimitive.Remove
        aria-label={`Remove ${attachment.name}`}
        className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
      >
        <CloseIcon className="h-3.5 w-3.5" />
      </AttachmentPrimitive.Remove>
    </AttachmentPrimitive.Root>
  );
}

function ScrollToBottom() {
  return (
    <ThreadPrimitive.ScrollToBottom
      className="absolute -top-5 left-1/2 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-card transition-opacity hover:text-primary-600 disabled:invisible"
      aria-label="Scroll to bottom"
    >
      <ArrowDownIcon className="h-4 w-4" />
    </ThreadPrimitive.ScrollToBottom>
  );
}

function UserMessage() {
  return (
    <MessagePrimitive.Root className="group flex flex-col items-end gap-1">
      {/* inside a message, ComposerPrimitive binds to that message's edit
          composer, so the same primitives serve both composers */}
      <AuiIf condition={(s) => s.message.composer.isEditing}>
        <UserEditComposer />
      </AuiIf>

      <AuiIf condition={(s) => !s.message.composer.isEditing}>
        {/* What the user attached, as file info rather than a preview: they
            already know what they sent, and a picture of it says nothing the
            name doesn't. Files the assistant produced are a different case and
            still render as parts, below. */}
        <AuiIf condition={(s) => s.message.attachments.length > 0}>
          <div className="flex max-w-[85%] flex-wrap justify-end gap-2">
            <MessagePrimitive.Attachments>
              {({ attachment }) => <SentAttachment attachment={attachment} />}
            </MessagePrimitive.Attachments>
          </div>
        </AuiIf>

        <AuiIf condition={(s) => s.message.content.length > 0}>
          <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary-600 px-4 py-2.5 text-base leading-relaxed text-white">
            <MessagePrimitive.Parts />
          </div>
        </AuiIf>

        <div className="flex items-center gap-1 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <MessageTime />

          <ActionBarPrimitive.Root hideWhenRunning className="flex gap-1">
            <ActionBarPrimitive.Copy
              className="rounded-md p-1.5 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Copy"
            >
              <AuiIf condition={(s) => s.message.isCopied}>
                <CheckIcon className="h-4 w-4" />
              </AuiIf>
              <AuiIf condition={(s) => !s.message.isCopied}>
                <CopyIcon className="h-4 w-4" />
              </AuiIf>
            </ActionBarPrimitive.Copy>

            {/* sending an edit re-runs from here. Today the new turn only lives
                for the session, see the branching note in CLAUDE.md */}
            <ActionBarPrimitive.Edit
              className="rounded-md p-1.5 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Edit"
            >
              <PencilIcon className="h-4 w-4" />
            </ActionBarPrimitive.Edit>
          </ActionBarPrimitive.Root>
        </div>
      </AuiIf>
    </MessagePrimitive.Root>
  );
}

function UserEditComposer() {
  return (
    <ComposerPrimitive.Root className="w-full max-w-[85%] rounded-2xl border border-gray-200 bg-white p-2 shadow-card focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500">
      <ComposerPrimitive.Input
        autoFocus
        rows={1}
        className="max-h-40 w-full resize-none bg-transparent px-2 py-1.5 text-base text-gray-900 outline-none"
      />

      <div className="flex justify-end gap-2 px-1 pt-1">
        <ComposerPrimitive.Cancel className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">
          Cancel
        </ComposerPrimitive.Cancel>
        <ComposerPrimitive.Send className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400">
          Send
        </ComposerPrimitive.Send>
      </div>
    </ComposerPrimitive.Root>
  );
}

// The backend can't report a client-side cancel: by then the connection is
// gone. assistant-ui marks the message incomplete/cancelled instead, and that
// is what we read here.
function Stopped() {
  const status = useAuiState((s) => s.message.status);

  if (status?.type !== 'incomplete' || status?.reason !== 'cancelled') return null;

  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-400">
      <StopIcon className="h-3 w-3" />
      <span>Stopped. Use regenerate to try again.</span>
    </div>
  );
}

// assistant-ui's web default Text part appends a literal " ●" while the part is
// in progress. It's static, so a slow stream looks stalled rather than live.
// Same idea, but a caret that blinks.
function AssistantText() {
  return (
    <div>
      <Markdown />
      <MessagePartPrimitive.InProgress>
        <span className="ml-0.5 inline-block h-3.5 w-0.5 translate-y-0.5 animate-pulse rounded-sm bg-gray-500 align-middle [animation-duration:600ms]" />
      </MessagePartPrimitive.InProgress>
    </div>
  );
}

// Shown while the run has produced nothing to render yet: at the start, and
// again between parts (a tool call, say). Carries the backend's latest progress
// line when there is one.
function Waiting({ status }) {
  const progress = useRunProgress();

  if (status?.type !== 'running') return null;

  return (
    <div className="flex items-center gap-2 py-1" role="status" aria-live="polite">
      <span className="flex items-center gap-1">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </span>

      {progress && <span className="text-xs text-gray-500">{progress}</span>}
    </div>
  );
}

// ErrorDetail is what actually went wrong, folded away: cryptic, but the only
// thing worth pasting into a bug report.
function ErrorDetail() {
  const detail = useAuiState((s) => s.message.status?.error?.detail);
  const [open, setOpen] = useState(false);

  if (!detail) return null;

  return (
    <span className="mt-1 block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="text-xs text-red-600 underline-offset-2 hover:underline"
      >
        {open ? '▾ Hide details' : '▸ Details'}
      </button>
      {open && (
        <code className="mt-1 block whitespace-pre-wrap break-words rounded bg-red-100/60 px-2 py-1 font-mono text-xs text-red-800">
          {detail}
        </code>
      )}
    </span>
  );
}

// The model's reasoning: open while it thinks, so you can watch it, and folded
// away once the answer starts, because it's context and not the answer. A
// manual toggle wins from then on, or it would fight the user.
function Thinking({ text, status }) {
  const running = status?.type === 'running';
  const [open, setOpen] = useState(running);
  const touched = useRef(false);

  useEffect(() => {
    if (!touched.current) setOpen(running);
  }, [running]);

  if (!text) return null;

  return (
    <div className="my-1 rounded-xl border border-gray-200 bg-gray-50/70">
      <button
        type="button"
        onClick={() => {
          touched.current = true;
          setOpen((shown) => !shown);
        }}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs font-medium text-gray-500 hover:text-gray-700"
      >
        <BrainIcon className="h-3.5 w-3.5" />
        <span>{running ? 'Thinking...' : 'Thought about it'}</span>
        <ChevronIcon className={`ml-auto h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* markdown, because a reasoning summary arrives as markdown: the
          provider writes bold run-in headers and lists, and rendering it raw
          showed the asterisks */}
      {open && (
        <div className="px-3 pb-2.5 text-xs leading-relaxed text-gray-600">
          <ReasoningMarkdown />
        </div>
      )}
    </div>
  );
}

// A run that stopped to ask something. The two kinds are not the same request
// and must not look alike: an approval is a decision the reader makes here,
// while a connect is an errand they run in the user portal before retrying.
// Drawing a connect as a yes/no invites someone to answer yes to a question
// that a yes cannot resolve.
function Interrupted({ data }) {
  const { answerQuestion } = useChats();
  const connect = data?.kind === 'connect';

  // The server records the decision on the question, so a reloaded chat shows
  // what was chosen rather than asking again.
  const answered = data?.approved !== undefined && data?.approved !== null;

  return (
    <div
      className={`my-2 rounded-xl border px-3 py-2.5 ${
        connect ? 'border-amber-200 bg-amber-50' : 'border-primary-200 bg-primary-50'
      }`}
      role="status"
    >
      <div className="flex items-start gap-2">
        <AlertIcon
          className={`mt-0.5 h-4 w-4 shrink-0 ${connect ? 'text-amber-500' : 'text-primary-600'}`}
        />

        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {connect ? 'Account needed' : 'Waiting for approval'}
          </p>

          <p className="mt-1 text-sm leading-relaxed text-gray-800">{data?.message}</p>

          {connect && (
            <a
              href="/user"
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-sm font-medium text-primary-600 underline underline-offset-2"
            >
              Open the user portal
            </a>
          )}

          {answered ? (
            <p className="mt-2 text-xs text-gray-500">
              {data.approved ? 'You allowed this.' : 'You declined this.'}
            </p>
          ) : (
            <div className="mt-2.5 flex gap-2">
              {/* A connect is not a yes/no: the account is linked elsewhere and
                  this only retries, so the label says retry rather than
                  inviting someone to approve their way past it. */}
              <button
                type="button"
                onClick={() => answerQuestion(data.toolCallId, true)}
                className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
              >
                {connect ? 'I have connected it' : 'Allow'}
              </button>

              <button
                type="button"
                onClick={() => answerQuestion(data.toolCallId, false)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                {connect ? 'Not now' : 'Deny'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// One tool invocation. Args and result are collapsed, the headline is what ran
// and whether it worked.
function ToolCall({ toolName, argsText, result, isError, status }) {
  const [open, setOpen] = useState(false);

  // A result means finished, whatever the message is doing: the message status
  // stays "running" while later text streams. With no result, the part's own
  // status decides, so a cancelled run stops saying "running" forever.
  const finished = result !== undefined;
  const running = !finished && status?.type === 'running';
  const label = isError ? 'failed' : finished ? 'done' : running ? 'running...' : 'stopped';

  return (
    <div className="my-1 rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((shown) => !shown)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-600 hover:text-gray-900"
      >
        <WrenchIcon className={`h-3.5 w-3.5 ${isError ? 'text-red-500' : 'text-gray-400'}`} />
        <span className="font-medium">{toolName}</span>
        <span className={isError ? 'text-red-600' : 'text-gray-400'}>{label}</span>
        <ChevronIcon className={`ml-auto h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="space-y-1.5 px-3 pb-2.5 text-xs text-gray-600">
          {argsText && <pre className="overflow-x-auto rounded bg-gray-50 p-2">{argsText}</pre>}
          {result !== undefined && (
            <pre className={`overflow-x-auto rounded p-2 ${isError ? 'bg-red-50 text-red-700' : 'bg-gray-50'}`}>
              {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function MessageTime() {
  const createdAt = useAuiState((s) => s.message.createdAt);
  if (!createdAt) return null;

  const date = new Date(createdAt);

  return (
    <time
      dateTime={date.toISOString()}
      title={date.toLocaleString()}
      className="px-1 text-xs tabular-nums"
    >
      {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </time>
  );
}

function AssistantMessage() {
  return (
    <MessagePrimitive.Root className="group flex flex-col gap-1.5">
      <div className="max-w-full text-base leading-relaxed text-gray-900">
        {/* plain text for now; markdown/mermaid rendering comes with the wiring.
            Empty is what shows while the run has produced nothing yet: without
            it assistant-ui falls back to an empty text part, which renders as a
            lone cursor blob. */}
        <MessagePrimitive.Parts
          components={{
            Empty: Waiting,
            Text: AssistantText,
            Reasoning: Thinking,
            Image: AttachedImage,
            File: AttachedFile,
            data: { by_name: { interrupt: Interrupted } },
            tools: { Fallback: ToolCall },
          }}
        />
      </div>

      {/* a failed run: the backend's error event, or a transport failure. The
          partial answer above it stays, marked incomplete. */}
      <MessagePrimitive.Error>
        <ErrorPrimitive.Root className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">
            <ErrorPrimitive.Message className="leading-relaxed" />
            <ErrorDetail />
          </span>
        </ErrorPrimitive.Root>
      </MessagePrimitive.Error>

      <Stopped />

      <ActionBarPrimitive.Root
        hideWhenRunning
        autohide="not-last"
        className="flex gap-1 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100"
      >
        <ActionBarPrimitive.Copy
          className="rounded-md p-1.5 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Copy"
        >
          {/* Copy swaps to a checkmark for a moment after a successful copy */}
          <AuiIf condition={(s) => s.message.isCopied}>
            <CheckIcon className="h-4 w-4" />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <CopyIcon className="h-4 w-4" />
          </AuiIf>
        </ActionBarPrimitive.Copy>

        <ActionBarPrimitive.Reload
          className="rounded-md p-1.5 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Regenerate"
        >
          <RefreshIcon className="h-4 w-4" />
        </ActionBarPrimitive.Reload>

        <BranchButton />
      </ActionBarPrimitive.Root>
    </MessagePrimitive.Root>
  );
}

// Branching copies the chat up to this answer into a chat of its own and opens
// it. No switcher, no sibling versions of a turn: the original keeps its
// history and the copy is where the conversation goes next.
function BranchButton() {
  const { forkChat } = useChats();
  const messageID = useAuiState((s) => s.message.id);

  return (
    <button
      type="button"
      onClick={() => forkChat(messageID)}
      title="Branch into a new chat"
      aria-label="Branch into a new chat"
      className="rounded-md p-1.5 hover:bg-gray-100 hover:text-gray-700"
    >
      <BranchIcon className="h-4 w-4" />
    </button>
  );
}
